import { History } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { PromptToneTemplate } from "@/api/types";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PromptHistoryDialog } from "@/components/ui/prompt-history-dialog";
import { SectionCard } from "@/components/ui/section-card";
import { Textarea } from "@/components/ui/textarea";
import {
  useRollbackTone,
  useToneHistory,
  useToneTemplates,
  useUpdateToneTemplate,
} from "@/features/prompt-tones/hooks/useToneTemplates";

export function PromptTonesPage() {
  const { t } = useTranslation("promptTones");
  const { data, isLoading, isError, error, refetch } = useToneTemplates();

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("title")} description={t("subtitle")} />

      <ul className="flex flex-col gap-3">
        {data?.map((tpl) => (
          <ToneCard key={tpl.key} template={tpl} />
        ))}
      </ul>
    </div>
  );
}

function ToneCard({ template }: { template: PromptToneTemplate }) {
  const { t } = useTranslation("promptTones");
  const update = useUpdateToneTemplate(template.key);
  const [name, setName] = useState(template.name);
  const [content, setContent] = useState(template.content);
  const [historyOpen, setHistoryOpen] = useState(false);
  const history = useToneHistory(template.key, historyOpen);
  const rollback = useRollbackTone(template.key);

  const isDirty = name !== template.name || content !== template.content;

  const friendlyName = t(`keys.${template.key}`, { defaultValue: name });

  async function handleSave() {
    try {
      await update.mutateAsync({ name, content });
      toast.success(`Tom "${friendlyName}" atualizado`);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <li className="contents">
      <SectionCard>
        <div className="flex items-center justify-between gap-2">
          <div>
            <code className="text-xs text-ink-500">{template.key}</code>
            <h2 className="text-base font-semibold text-ink-950">
              {t(`keys.${template.key}`, { defaultValue: template.name })}
            </h2>
          </div>
          <span className="text-xs text-ink-500">
            Atualizado em {new Date(template.updated_at).toLocaleString("pt-BR")}
          </span>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`tone-name-${template.key}`}>{t("form.name")}</Label>
            <Input
              id={`tone-name-${template.key}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor={`tone-content-${template.key}`}>
                {t("form.content")}
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setHistoryOpen(true)}
              >
                <History className="h-3.5 w-3.5" />
                Histórico
              </Button>
            </div>
            <Textarea
              id={`tone-content-${template.key}`}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
            />
            <p className="text-xs text-ink-500">{t("form.contentHint")}</p>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || update.isPending}
            >
              {t("form.save")}
            </Button>
          </div>
        </div>
      </SectionCard>

      <PromptHistoryDialog
        resourceLabel={`tom "${friendlyName}"`}
        versions={history.data ?? []}
        isLoading={history.isLoading}
        currentPrompt={template.content}
        onRollback={(version) => rollback.mutateAsync({ version })}
        isRollingBack={rollback.isPending}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </li>
  );
}
